import { identity, isTruthy, Predicate, Project } from '../core';
import { Extension } from '../extension';

type CompoundEvaluator<T> = (a: T, b: T) => number[];

class IntersectionResult<T, S> {
  public readonly inLeft: T[] = [];
  public readonly inBothLeftType: T[] = [];
  public readonly inBothRightType: S[] = [];
  public readonly inRight: S[] = [];

  get areEqualByIntersection(): boolean {
    return this.inLeft.isEmpty() && this.inRight.isEmpty() && this.inBothLeftType.length === this.inBothRightType.length;
  }
}

function getResultMultiplier(ascending: boolean): number {
  return ascending ? 1 : -1;
}

export {};

declare global {
  export interface Array<T> {
    flatten<T>(this: T[][]): T[];
    compoundSort<T>(this: T[], ascending: boolean, evaluator: CompoundEvaluator<T>): T[];
    compoundSortAscending<T>(this: T[], evaluator: CompoundEvaluator<T>): T[];
    compoundSortDescending<T>(this: T[], evaluator: CompoundEvaluator<T>): T[];
    sortBy<T, S>(this: T[], propertySelector: Project<T, S>, ascending: boolean): T[];
    sortByAscending<T, S>(this: T[], propertySelector: Project<T, S>): T[];
    sortByDescending<T, S>(this: T[], propertySelector: Project<T, S>): T[];
    groupBy<T, S>(this: T[], propertySelector: Project<T, S>): Map<S, T[]>;
    groupByToRecord<T, S extends string | number | symbol>(this: T[], propertySelector: Project<T, S>): Record<S, T[]>;
    uniqueBy<T, S>(this: T[], identitySelector: (element: T) => S): T[];
    unique<T, S>(this: T[]): T[];
    filterTruthy<T>(this: T[]): T[];
    first<T>(this: T[]): T;
    firstOrNull<T>(this: T[]): T | null;
    last<T>(this: T[]): T;
    lastOrNull<T>(this: T[]): T | null;
    anyItems<T>(this: T[]): boolean;
    isEmpty<T>(this: T[]): boolean;
    intersect<S, Key>(this: T[], right: S[], keySelector: Project<T, Key>, rhsKeySelector: Project<S, Key>): IntersectionResult<T, S>;
    subtract<T, S>(this: T[], elements: T[], identitySelector: Project<T, S>): T[];
    includesAny<T>(this: T[], items: T[]): boolean;
    mapToKey<T, K extends keyof T>(this: T[], key: K): T[K][];
    remove<T>(this: T[], element: T): boolean;
    removeMatching<T>(this: T[], predicate: Predicate<T>): boolean;
  }
}

class ArrayExtensions {
  static {
    console.log('Extensions registered');
  }

  @Extension(Array, 'flatten')
  private static flatten<T>(array: T[][]): T[] {
    return array.reduce((current, next) => {
      current.push(...next);
      return current;
    }, []);
  }

  @Extension(Array, 'compoundSort')
  private static compoundSort<T>(array: T[], ascending: boolean, evaluator: CompoundEvaluator<T>): T[] {
    const resultMultiplier = getResultMultiplier(ascending);

    return array.slice().sort((a, b) => {
      const evaluation = evaluator(a, b);

      for (const evaluationResult of evaluation) {
        if (evaluationResult !== 0) {
          return resultMultiplier * evaluationResult;
        }
      }

      return 0;
    });
  }

  @Extension(Array, 'compoundSortAscending')
  private static compoundSortAscending<T>(array: T[], evaluator: CompoundEvaluator<T>): T[] {
    return ArrayExtensions.compoundSort(array, true, evaluator);
  }

  @Extension(Array, 'compoundSortDescending')
  private static compoundSortDescending<T>(array: T[], evaluator: CompoundEvaluator<T>): T[] {
    return ArrayExtensions.compoundSort(array, false, evaluator);
  }

  @Extension(Array, 'sortBy')
  private static sortBy<T, S>(array: T[], propertySelector: Project<T, S>, ascending = true): T[] {
    const resultMultiplier = getResultMultiplier(ascending);
    return array.slice().sort((a, b) => {
      const propA = propertySelector(a);
      const propB = propertySelector(b);

      return resultMultiplier * (propA > propB ? 1 : propA === propB ? 0 : -1);
    });
  }

  @Extension(Array, 'sortByAscending')
  private static sortByAscending<T, S>(array: T[], propertySelector: Project<T, S>): T[] {
    return ArrayExtensions.sortBy(array, propertySelector, true);
  }

  @Extension(Array, 'sortByDescending')
  private static sortByDescending<T, S>(array: T[], propertySelector: Project<T, S>): T[] {
    return ArrayExtensions.sortBy(array, propertySelector, false);
  }

  @Extension(Array, 'groupBy')
  private static groupBy<T, S>(array: T[], propertySelector: Project<T, S>): Map<S, T[]> {
    const result = new Map<S, T[]>();

    for (const element of array) {
      const groupByProperty = propertySelector(element);

      const existing = result.get(groupByProperty);

      if (existing != null) {
        existing.push(element);
      } else {
        result.set(groupByProperty, [element]);
      }
    }

    return result;
  }

  @Extension(Array, 'groupByToRecord')
  private static groupByToRecord<T, S extends string | number | symbol>(array: T[], propertySelector: Project<T, S>): Record<S, T[]> {
    // @ts-ignore Empty object is not compatible as record, but it will be filled up
    const result: Record<S, T[]> = {};

    for (const element of array) {
      const groupByProperty = propertySelector(element);

      if (result[groupByProperty]) {
        result[groupByProperty].push(element);
      } else {
        result[groupByProperty] = [element];
      }
    }

    return result;
  }

  @Extension(Array, 'uniqueBy')
  private static uniqueBy<T, S>(array: T[], identitySelector: (element: T) => S): T[] {
    const ret: T[] = [];
    array.forEach((it) => {
      if (!ret.find((it2) => identitySelector(it) === identitySelector(it2))) {
        ret.push(it);
      }
    });
    return ret;
  }

  @Extension(Array, 'unique')
  private static unique<T>(array: T[]): T[] {
    return ArrayExtensions.uniqueBy(array, identity());
  }

  @Extension(Array, 'filterTruthy')
  private static filterTruthy<T>(array: T[]): T[] {
    return array.filter(isTruthy);
  }

  @Extension(Array, 'first')
  private static first<T>(array: T[]): T {
    if (!array.length) {
      throw Error(`Can't get first element of an empty array`);
    }
    return array[0];
  }

  @Extension(Array, 'firstOrNull')
  private static firstOrNull<T>(array: T[]): T | null {
    return array[0] ?? null;
  }

  @Extension(Array, 'last')
  private static last<T>(array: T[]): T {
    if (!array.length) {
      throw Error(`Can't get last element of an empty array`);
    }
    return array[array.length - 1];
  }

  @Extension(Array, 'lastOrNull')
  private static lastOrNull<T>(array: T[]): T | null {
    return array[array.length - 1] ?? null;
  }

  @Extension(Array, 'anyItems')
  private static anyItems<T>(array: T[]): boolean {
    return array.length > 0;
  }

  @Extension(Array, 'isEmpty')
  private static isEmpty<T>(array: T[]): boolean {
    return array.length === 0;
  }

  @Extension(Array, 'intersect')
  private static intersect<T, S, Key>(
    left: T[],
    right: S[],
    leftKeySelector: Project<T, Key>,
    rightKeySelector: Project<S, Key>
  ): IntersectionResult<T, S> {
    const ret = new IntersectionResult<T, S>();

    left.forEach((leftElement) => {
      const find = right.find((rightElement) => leftKeySelector(leftElement) === rightKeySelector(rightElement));
      if (!find) {
        ret.inLeft.push(leftElement);
      } else {
        ret.inBothLeftType.push(leftElement);
      }
    });
    right.forEach((rightElement) => {
      const find = left.find((leftElement) => leftKeySelector(leftElement) === rightKeySelector(rightElement));
      if (!find) {
        ret.inRight.push(rightElement);
      } else {
        ret.inBothRightType.push(rightElement);
      }
    });
    return ret;
  }

  @Extension(Array, 'subtract')
  private static subtract<T, S>(from: T[], elements: T[], identitySelector: Project<T, S>): T[] {
    return ArrayExtensions.intersect(from, elements, identitySelector, identitySelector).inLeft.slice();
  }

  @Extension(Array, 'includesAny')
  private static includesAny<T>(array: T[], items: T[]): boolean {
    return ArrayExtensions.intersect(array, items, identity(), identity()).inBothRightType.anyItems();
  }

  @Extension(Array, 'mapToKey')
  private static mapToKey<T, K extends keyof T>(array: T[], key: K): T[K][] {
    return array.map((it) => it[key]);
  }

  @Extension(Array, 'remove')
  private static remove<T>(array: T[], element: T): boolean {
    const idx = array.indexOf(element);
    if (idx !== -1) {
      array.splice(idx, 1);
      return true;
    }
    return false;
  }

  @Extension(Array, 'removeMatching')
  private static removeMatching<T>(array: T[], predicate: Predicate<T>): boolean {
    // @ts-ignore
    const idx = array.indexOf(array.find((it) => predicate(it)));
    if (idx !== -1) {
      array.splice(idx, 1);
      return true;
    }
    return false;
  }
}

export function registerExtensions(): ArrayExtensions {
  // This function does nothing, but by calling it TS won't tree shake the unused import
  // as the extensions only register if the file is executed
  // eslint-disable-next-line no-console
  return ArrayExtensions;
}
